import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label, Card } from './ui'
import { useAuthStore } from '../stores/authStore'
import useDraftStore from '../stores/draftStore'
import api from '../services/api'

export default function AddPharmacyToProgramModal({ isOpen, onClose, program, onSave }) {
  const [pharmacies, setPharmacies] = useState([])
  const [selectedPharmacy, setSelectedPharmacy] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  
  // Pharmacy configuration form
  const [pharmacyCode, setPharmacyCode] = useState('')
  const [currentPrice, setCurrentPrice] = useState('')
  const [drugs, setDrugs] = useState([{
    drug_name: '',
    pharmacy_drug_name: '',
    current_price: '',
    dispense_quantity: '',
    dispense_unit: 'Tablet',
    days: 30
  }])
  
  const { currentClinicId } = useAuthStore()
  const { addChange } = useDraftStore()

  // Load available pharmacies
  useEffect(() => {
    if (isOpen && currentClinicId) {
      loadPharmacies()
    }
  }, [isOpen, currentClinicId])

  // Initialize form when pharmacy is selected
  useEffect(() => {
    if (selectedPharmacy && program) {
      // Pre-fill drug name from program
      setDrugs([{
        drug_name: program.drug_name || '',
        pharmacy_drug_name: program.drug_name || '',
        current_price: '',
        dispense_quantity: '',
        dispense_unit: 'Tablet',
        days: 30
      }])
    }
  }, [selectedPharmacy, program])

  const loadPharmacies = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get('/pharmacy', {
        params: { 
          limit: 1000, 
          include_disabled: false 
        }
      })
      
      // Filter out pharmacies already in the program
      const existingPharmacyIds = program?.pharmacies?.map(p => p.pharmacy_ncpdpid) || []
      const availablePharmacies = (response.data.pharmacies || []).filter(
        p => !existingPharmacyIds.includes(p.pharmacy_ncpdpid)
      )
      
      setPharmacies(availablePharmacies)
    } catch (error) {
      console.error('[ADD PHARMACY] Error loading pharmacies:', error)
      setError('Failed to load pharmacies')
    } finally {
      setLoading(false)
    }
  }

  const addDrugField = () => {
    setDrugs([...drugs, {
      drug_name: program?.drug_name || '',
      pharmacy_drug_name: '',
      current_price: '',
      dispense_quantity: '',
      dispense_unit: 'Tablet',
      days: 30
    }])
  }

  const removeDrugField = (index) => {
    if (drugs.length > 1) {
      setDrugs(drugs.filter((_, i) => i !== index))
    }
  }

  const updateDrugField = (index, field, value) => {
    const updated = [...drugs]
    updated[index][field] = value
    setDrugs(updated)
  }

  const validateForm = () => {
    if (!selectedPharmacy) {
      setError('Please select a pharmacy')
      return false
    }

    // Validate each drug entry
    for (let i = 0; i < drugs.length; i++) {
      const drug = drugs[i]
      if (!drug.drug_name || !drug.pharmacy_drug_name) {
        setError(`Drug ${i + 1}: Drug name is required`)
        return false
      }
      if (!drug.current_price || parseFloat(drug.current_price) < 0) {
        setError(`Drug ${i + 1}: Valid price is required`)
        return false
      }
      if (!drug.dispense_quantity || parseFloat(drug.dispense_quantity) <= 0) {
        setError(`Drug ${i + 1}: Valid dispense quantity is required`)
        return false
      }
      if (!drug.dispense_unit) {
        setError(`Drug ${i + 1}: Dispense unit is required`)
        return false
      }
    }

    return true
  }

  const handleSave = () => {
    if (!validateForm()) return

    try {
      setSaving(true)
      setError(null)

      // Build pharmacy configuration
      const pharmacyConfig = {
        pharmacy_name: selectedPharmacy.pharmacy_name,
        pharmacy_ncpdpid: selectedPharmacy.pharmacy_ncpdpid,
        pharmacy_code: pharmacyCode || undefined,
        current_price: currentPrice ? parseFloat(currentPrice) : undefined,
        drugs: drugs.map(d => ({
          drug_name: d.drug_name,
          pharmacy_drug_name: d.pharmacy_drug_name,
          current_price: parseFloat(d.current_price),
          dispense_quantity: parseFloat(d.dispense_quantity),
          dispense_unit: d.dispense_unit,
          days: parseInt(d.days) || 30
        }))
      }

      // Create updated program with new pharmacy
      const updatedProgram = {
        ...program,
        pharmacies: [...(program.pharmacies || []), pharmacyConfig]
      }

      // Add change to draft store
      const drugCount = drugs.length
      const totalPrice = drugs.reduce((sum, d) => sum + parseFloat(d.current_price), 0)
      
      addChange({
        resource_type: 'drug_program',
        resource_id: program.entity_id || program._id,
        operation: 'update',
        original_data: program,
        new_data: updatedProgram,
        description: `Add Pharmacy: ${selectedPharmacy.pharmacy_name} to "${program.drug_name}" (${drugCount} drug${drugCount !== 1 ? 's' : ''}, $${totalPrice.toFixed(2)} total)`
      })

      console.log('[ADD PHARMACY] Added pharmacy to program in drafts')

      // Notify parent
      onSave?.()
      handleClose()
    } catch (error) {
      console.error('[ADD PHARMACY] Error saving:', error)
      setError('Failed to add pharmacy to program')
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setSelectedPharmacy(null)
    setPharmacyCode('')
    setCurrentPrice('')
    setDrugs([{
      drug_name: '',
      pharmacy_drug_name: '',
      current_price: '',
      dispense_quantity: '',
      dispense_unit: 'Tablet',
      days: 30
    }])
    setSearchTerm('')
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  const filteredPharmacies = pharmacies.filter(p =>
    p.pharmacy_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.pharmacy_ncpdpid.toString().includes(searchTerm)
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Pharmacy to {program?.drug_name}</DialogTitle>
          <DialogDescription>
            Select a pharmacy and configure drug details for this program
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Step 1: Select Pharmacy */}
          {!selectedPharmacy ? (
            <div>
              <h3 className="font-semibold mb-3">Step 1: Select Pharmacy</h3>
              
              <Input
                type="text"
                placeholder="Search pharmacies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mb-3"
              />

              {loading ? (
                <div className="text-center text-muted-foreground py-8">
                  Loading pharmacies...
                </div>
              ) : filteredPharmacies.length === 0 ? (
                <div className="text-center text-muted-foreground py-8 border rounded-lg bg-muted/20">
                  {searchTerm ? 'No pharmacies match your search' : 'No available pharmacies'}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                  {filteredPharmacies.map((pharmacy) => (
                    <Card
                      key={pharmacy._id}
                      className="p-4 cursor-pointer hover:border-primary transition-colors"
                      onClick={() => setSelectedPharmacy(pharmacy)}
                    >
                      <div className="font-medium">{pharmacy.pharmacy_name}</div>
                      <div className="text-sm text-muted-foreground">
                        NCPDPID: {pharmacy.pharmacy_ncpdpid}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Gateway: {pharmacy.pharmacy_gateway || 'N/A'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {pharmacy.states?.length || 0} states
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Step 2: Configure Pharmacy Details */
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-3 border-b">
                <div>
                  <h3 className="font-semibold">Step 2: Configure Pharmacy Details</h3>
                  <p className="text-sm text-muted-foreground">
                    Selected: {selectedPharmacy.pharmacy_name}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedPharmacy(null)}
                >
                  Change Pharmacy
                </Button>
              </div>

              {/* Optional Pharmacy Config */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pharmacy Code (Optional)</Label>
                  <Input
                    type="text"
                    value={pharmacyCode}
                    onChange={(e) => setPharmacyCode(e.target.value)}
                    placeholder="Internal pharmacy code"
                  />
                </div>
                <div>
                  <Label>Program Price (Optional)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentPrice}
                    onChange={(e) => setCurrentPrice(e.target.value)}
                    placeholder="Overall program price"
                  />
                </div>
              </div>

              {/* Drug Configurations */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-semibold">Drug Details (Required)</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addDrugField}
                  >
                    + Add Another Drug
                  </Button>
                </div>

                {drugs.map((drug, index) => (
                  <Card key={index} className="p-4 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">Drug {index + 1}</h4>
                      {drugs.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDrugField(index)}
                          className="text-destructive"
                        >
                          Remove
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Drug Name *</Label>
                        <Input
                          type="text"
                          value={drug.drug_name}
                          onChange={(e) => updateDrugField(index, 'drug_name', e.target.value)}
                          placeholder="e.g., Semaglutide"
                        />
                      </div>
                      <div>
                        <Label>Pharmacy Drug Name *</Label>
                        <Input
                          type="text"
                          value={drug.pharmacy_drug_name}
                          onChange={(e) => updateDrugField(index, 'pharmacy_drug_name', e.target.value)}
                          placeholder="e.g., Semaglutide 2.5mg"
                        />
                      </div>
                      <div>
                        <Label>Price *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={drug.current_price}
                          onChange={(e) => updateDrugField(index, 'current_price', e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label>Dispense Quantity *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={drug.dispense_quantity}
                          onChange={(e) => updateDrugField(index, 'dispense_quantity', e.target.value)}
                          placeholder="e.g., 30"
                        />
                      </div>
                      <div>
                        <Label>Dispense Unit *</Label>
                        <Input
                          type="text"
                          value={drug.dispense_unit}
                          onChange={(e) => updateDrugField(index, 'dispense_unit', e.target.value)}
                          placeholder="e.g., Tablet, mL"
                        />
                      </div>
                      <div>
                        <Label>Days Supply</Label>
                        <Input
                          type="number"
                          min="1"
                          value={drug.days}
                          onChange={(e) => updateDrugField(index, 'days', e.target.value)}
                          placeholder="30"
                        />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          {selectedPharmacy && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Add Pharmacy to Program'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

